import { useState, useEffect, useRef } from "react";

export default function Contact() {
  const ref = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    if (ref.current) ref.current.querySelectorAll(".animate-on-scroll").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const inputClass = `w-full bg-red-50 border border-red-100 text-slate-800 placeholder-slate-400
    rounded-xl px-5 py-4 text-sm focus:outline-none focus:border-red-500 focus:bg-white
    transition-all duration-300`;

  return (
    <section id="contact" className="py-28 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <div className="inline-flex items-center gap-2 text-red-600 text-xs font-semibold tracking-widest uppercase
            bg-red-50 border border-red-100 px-4 py-2 rounded-full mb-6">
            Get In Touch
          </div>
          <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 'clamp(2.2rem,5vw,3.5rem)' }}
            className="text-red-950">
            START YOUR <span className="text-blue-700">DREAM PROJECT</span> TODAY
          </h2>
          <p className="text-slate-500 mt-4 max-w-xl mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Tell us about your land or commercial space requirements and our experts will get back to you within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact info - Updated with Dehradun address */}
          <div className="lg:col-span-2 animate-on-scroll space-y-6">
            {[
              {
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>,
                label: "Our Office",
                value: "Khasra No. 759/B, Upper Tunwala\nPushp Vihar, Chaktonwala Grant\nDehradun, Uttarakhand – 248005",
              },
              {
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>,
                label: "Call / WhatsApp",
                value: "+91 96674 04020\n096674 04020",
              },
              {
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>,
                label: "Email Us",
                value: "nexedgeinfra@gmail.com\ninfo@nexedgeinfra.com",
              },
              {
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>,
                label: "Business Hours",
                value: "Mon – Sat: 10:00 AM – 7:00 PM\nSunday: 11:00 AM – 4:00 PM",
              },
            ].map(item => (
              <div key={item.label} className="flex gap-5 p-5 rounded-2xl border border-red-50 hover:border-red-200 hover:bg-red-50/50 transition-all duration-300 group">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gradient-to-br group-hover:from-red-900 group-hover:to-blue-900 transition-all duration-300">
                  <svg className="w-5 h-5 text-red-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {item.icon}
                  </svg>
                </div>
                <div>
                  <div className="text-red-950 font-semibold text-sm mb-1">{item.label}</div>
                  <div className="text-slate-500 text-sm whitespace-pre-line leading-relaxed">{item.value}</div>
                </div>
              </div>
            ))}

            {/* Social Media Links */}
            <div className="flex gap-4 pt-4">
              <a href="https://www.instagram.com/nexedgeinfra" target="_blank" rel="noopener noreferrer" 
                className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center hover:bg-gradient-to-br hover:from-red-900 hover:to-blue-900 transition-all duration-300 group">
                <svg className="w-5 h-5 text-red-600 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              <a href="https://www.youtube.com/@nexedgeinfra" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center hover:bg-gradient-to-br hover:from-red-900 hover:to-blue-900 transition-all duration-300 group">
                <svg className="w-5 h-5 text-red-600 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a href="https://nexedgeinfra.com" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center hover:bg-gradient-to-br hover:from-red-900 hover:to-blue-900 transition-all duration-300 group">
                <svg className="w-5 h-5 text-red-600 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.66 0 3-4 3-9s-1.34-9-3-9m0 18c-1.66 0-3-4-3-9s1.34-9 3-9"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Form - Updated colors */}
          <div className="lg:col-span-3 animate-on-scroll" style={{ transitionDelay: '150ms' }}>
            {submitted ? (
              <div className="bg-gradient-to-br from-red-50 to-blue-50 border border-red-200 rounded-3xl p-16 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-red-900 to-blue-900 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-red-200">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <h3 className="text-red-950 font-bold text-2xl mb-3"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  MESSAGE SENT!
                </h3>
                <p className="text-slate-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Thank you for reaching out. Our team will contact you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white border border-red-100 rounded-3xl p-8 shadow-xl shadow-red-50">
                <div className="grid sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-red-950 text-xs font-semibold tracking-wide uppercase mb-2">Full Name *</label>
                    <input required className={inputClass} placeholder="Rahul Sharma"
                      value={form.name} onChange={e => setForm({...form, name: e.target.value})}/>
                  </div>
                  <div>
                    <label className="block text-red-950 text-xs font-semibold tracking-wide uppercase mb-2">Email *</label>
                    <input required type="email" className={inputClass} placeholder="rahul@company.com"
                      value={form.email} onChange={e => setForm({...form, email: e.target.value})}/>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-red-950 text-xs font-semibold tracking-wide uppercase mb-2">Phone</label>
                    <input className={inputClass} placeholder="+91 96674 04020"
                      value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}/>
                  </div>
                  <div>
                    <label className="block text-red-950 text-xs font-semibold tracking-wide uppercase mb-2">Property Type</label>
                    <select className={inputClass}
                      value={form.service} onChange={e => setForm({...form, service: e.target.value})}>
                      <option value="">Select property type</option>
                      <option>Residential Plot</option>
                      <option>Commercial Space</option>
                      <option>Retail Shop</option>
                      <option>Investment Property</option>
                      <option>Land for Development</option>
                    </select>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-red-950 text-xs font-semibold tracking-wide uppercase mb-2">Your Requirements *</label>
                  <textarea required rows={5} className={inputClass} placeholder="Tell us about your requirements — plot size, budget, location preference, timeline..."
                    value={form.message} onChange={e => setForm({...form, message: e.target.value})}/>
                </div>
                <button type="submit"
                  className="w-full bg-gradient-to-r from-red-900 to-blue-900 hover:from-red-800 hover:to-blue-800 text-white py-4 rounded-xl
                  font-semibold text-base transition-all duration-300 shadow-xl shadow-red-900/30 flex items-center justify-center gap-3">
                  Send Message
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                  </svg>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}